import { ApolloError, useMutation } from '@apollo/client'
import { DocumentTemplateView } from '@deltek/specification-client/core/Models/documentTemplate'
import {
    SetProjectContentItemIsHiddenInput,
    ViewItemReorderInput,
} from '@deltek/specification-client/core/Models/projectProductFamily'
import { ViewItemReorderPTPInput } from '@deltek/specification-client/core/Models/projectProductTypeProperty'
import { summarizeApolloError } from '@deltek/specification-client/core/Utilities/apolloUtils'
import {
    ReorderFamilyContent,
    ReorderProductTypeContent,
    SetProjectContentItemIsHidden,
} from 'api/graphQL/mutations/projectProductFamily.mutations'
import {
    handleDTVReorder,
    setUpdatedDtv,
} from 'api/graphQL/reactiveVars/workspaceContent'
import { useActiveTab } from 'components/WorkspaceEditor/Workspace2Store'
import { useCallback } from 'react'

export const useShowHideMutation = () => {
    const handleFetchError = useCallback((error: ApolloError) => {
        const summarizedError = summarizeApolloError(
            error,
            'WorkspaceEditor-deactivateContentItemElement'
        )
        console.error(summarizedError)
    }, [])

    // Mutation to show/hide the content item elements.
    const [setProjectContentItemHidden] = useMutation<
        { setProjectContentItemIsHidden: Partial<DocumentTemplateView> },
        {
            SetProjectContentItemIsHiddenInput: Partial<
                SetProjectContentItemIsHiddenInput
            >
        }
    >(SetProjectContentItemIsHidden, {
        fetchPolicy: 'no-cache',
        onCompleted: (data) => {
            setUpdatedDtv(data.setProjectContentItemIsHidden)
        },
        onError: handleFetchError,
    })

    return {
        setProjectContentItemHidden,
    }
}

export const useReorderFamilyTemplatesMutation = () => {
    const handleFetchError = useCallback((error: ApolloError) => {
        const summarizedError = summarizeApolloError(
            error,
            'WorkspaceEditor-reorderFamilyTemplates'
        )
        console.error(summarizedError)
    }, [])

    // Mutation to reorder family templates.
    const [reorderFamilyTemplates] = useMutation<
        { reOrderFamilyContent: Partial<DocumentTemplateView> },
        { reOrderFamilyContentInput: Partial<ViewItemReorderInput> }
    >(ReorderFamilyContent, {
        fetchPolicy: 'no-cache',
        onCompleted: (data: {
            reOrderFamilyContent: Partial<DocumentTemplateView>
        }) => {
            setUpdatedDtv(data?.reOrderFamilyContent)
        },
        onError: handleFetchError,
    })

    return {
        reorderFamilyTemplates,
    }
}

export const useReorderProductTypeTemplatesMutation = () => {
    const activeTab = useActiveTab()

    const handleFetchError = useCallback((error: ApolloError) => {
        const summarizedError = summarizeApolloError(
            error,
            'WorkspaceEditor-reorderProductTypeTemplates'
        )
        console.error(summarizedError)
    }, [])

    // Mutation to reorder product type templates.
    const [reorderProductTypeTemplates] = useMutation<
        { reOrderPTContent: Partial<DocumentTemplateView> },
        { reOrderPTContentInput: Partial<ViewItemReorderPTPInput> }
    >(ReorderProductTypeContent, {
        fetchPolicy: 'no-cache',
        onCompleted: (data: {
            reOrderPTContent: Partial<DocumentTemplateView>
        }) => {
            handleDTVReorder(
                activeTab,
                data.reOrderPTContent as DocumentTemplateView
            )
        },
        onError: handleFetchError,
    })

    return {
        reorderProductTypeTemplates,
    }
}
